import React, { useState } from "react";
import Meta from "../../components/Meta";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../util/auth";
import { useForm } from "react-hook-form";
import {
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
  Fade,
  Slide
} from "@mui/material";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import GoogleIcon from '@mui/icons-material/Google';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import queryString from "query-string";

// Keyframe animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  position: 'relative',
  overflow: 'hidden',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)'
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  }
}));

const BrandSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(8),
  color: '#fff',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4),
    minHeight: '30vh',
  },
}));

const FloatingShape = styled(Box)(({ delay = 0 }) => ({
  position: 'absolute',
  borderRadius: '50%',
  opacity: 0.1,
  animation: `${float} 6s ease-in-out infinite`,
  animationDelay: `${delay}s`,
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  backgroundColor: theme.palette.mode === 'dark' ? '#0F172A' : '#fff',
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('md')]: {
    minHeight: '70vh',
    borderTopLeftRadius: '32px',
    borderTopRightRadius: '32px',
    marginTop: '-32px',
    backgroundColor: theme.palette.mode === 'dark' ? '#1E293B' : '#fff',
  },
}));

const FormCard = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '480px',
  animation: `${fadeInUp} 0.6s ease-out`,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha('#fff', 0.05)
      : alpha('#000', 0.02),
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha('#fff', 0.08)
        : alpha('#000', 0.04),
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha('#fff', 0.08)
        : alpha('#667eea', 0.05),
      '& fieldset': {
        borderColor: '#667eea',
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#667eea',
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '12px',
  padding: '14px 28px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  color: '#fff',
  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    background: alpha('#667eea', 0.5),
    color: alpha('#fff', 0.7),
  },
}));

const GoogleButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: '14px 28px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  border: `2px solid ${theme.palette.mode === 'dark' ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
  color: theme.palette.mode === 'dark' ? '#fff' : '#1a1a1a',
  backgroundColor: theme.palette.mode === 'dark' ? alpha('#fff', 0.05) : '#fff',
  transition: 'all 0.3s ease',
  '&:hover': {
    border: `2px solid ${theme.palette.mode === 'dark' ? alpha('#fff', 0.2) : alpha('#000', 0.2)}`,
    backgroundColor: theme.palette.mode === 'dark' ? alpha('#fff', 0.08) : alpha('#000', 0.02),
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
}));

const FeatureItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '20px',
  '& svg': {
    fontSize: '32px',
  },
});

function SignIn() {
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm();
  const auth = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      await auth.signInWithGoogle();
      const params = queryString.parse(location.search);
      navigate(params.next || '/bonus-watcher');
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    try {
      await auth.signIn(data.email, data.password);
      const params = queryString.parse(location.search);
      navigate(params.next || '/bonus-watcher');
    } catch (error) {
      console.error("Error during sign in:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Meta title="Sign In - Welcome Back" />
      <PageContainer>
        {/* Brand/Marketing Section */}
        <BrandSection>
          {/* Floating background shapes */}
          <FloatingShape
            delay={0}
            sx={{
              width: '300px',
              height: '300px',
              background: 'rgba(255, 255, 255, 0.1)',
              top: '10%',
              left: '10%',
            }}
          />
          <FloatingShape
            delay={2}
            sx={{
              width: '200px',
              height: '200px',
              background: 'rgba(255, 255, 255, 0.1)',
              bottom: '20%',
              right: '15%',
            }}
          />
          <FloatingShape
            delay={4}
            sx={{
              width: '150px',
              height: '150px',
              background: 'rgba(255, 255, 255, 0.1)',
              top: '50%',
              right: '25%',
            }}
          />

          <Fade in timeout={800}>
            <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center', maxWidth: '500px' }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: '2rem', md: '3rem' },
                  background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.8) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                BonusWatcher
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  fontSize: { xs: '1.1rem', md: '1.5rem' },
                  fontWeight: 500,
                }}
              >
                Welcome Back to Your Bonus Dashboard
              </Typography>

              {!isMobile && (
                <Box sx={{ mt: 6 }}>
                  <FeatureItem>
                    <TrendingUpIcon />
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Track Progress
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Monitor all your active bonuses
                      </Typography>
                    </Box>
                  </FeatureItem>

                  <FeatureItem>
                    <SpeedIcon />
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Stay On Track
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Get reminders for important deadlines
                      </Typography>
                    </Box>
                  </FeatureItem>

                  <FeatureItem>
                    <SecurityIcon />
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Secure Access
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Your data is protected and private
                      </Typography>
                    </Box>
                  </FeatureItem>
                </Box>
              )}
            </Box>
          </Fade>
        </BrandSection>

        {/* Form Section */}
        <FormSection>
          <FormCard>
            <Slide direction="left" in timeout={600}>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: theme.palette.mode === 'dark' ? '#fff' : '#1a1a1a',
                  }}
                >
                  Welcome back
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    color: theme.palette.mode === 'dark' ? alpha('#fff', 0.7) : alpha('#000', 0.6),
                  }}
                >
                  Sign in to continue managing your bonuses
                </Typography>

                {/* Google Sign In */}
                <GoogleButton
                  fullWidth
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  startIcon={<GoogleIcon />}
                >
                  Continue with Google
                </GoogleButton>

                <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      px: 2,
                      color: theme.palette.mode === 'dark' ? alpha('#fff', 0.5) : alpha('#000', 0.5),
                    }}
                  >
                    or
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>

                {/* Email/Password Form */}
                <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                  <StyledTextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />

                  <StyledTextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters",
                      },
                    })}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {error && (
                    <Typography
                      color="error"
                      variant="body2"
                      sx={{ mt: 2, textAlign: 'center' }}
                    >
                      {error}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Link
                      to="/auth/forgotpass"
                      style={{
                        color: '#667eea',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      Forgot password?
                    </Link>
                  </Box>

                  <GradientButton
                    type="submit"
                    fullWidth
                    disabled={loading}
                    sx={{ mt: 3, mb: 2 }}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </GradientButton>

                  <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.mode === 'dark' ? alpha('#fff', 0.7) : alpha('#000', 0.6),
                      }}
                    >
                      Don't have an account?{' '}
                      <Link
                        to="/auth/signup"
                        style={{
                          color: '#667eea',
                          textDecoration: 'none',
                          fontWeight: 600,
                        }}
                      >
                        Sign up
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Slide>
          </FormCard>
        </FormSection>
      </PageContainer>
    </>
  );
}

export default SignIn;
